import { useEffect, useState } from 'react'
import type { ProductLabel } from '@/api/productLabelsApi'
import { useSetLabelUsage } from '@/hooks/useProductLabels'
import { useAuth } from '@/providers/AuthProvider'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { LABEL_SIGNATURE_MEANINGS } from '@/lib/productLabels'
import { SignatureDialog } from '@/components/signature/SignatureDialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

/** Common reasons, offered as suggestions; any text is accepted. */
const DISCARD_REASONS = ['Damaged', 'Misprinted', 'Smudged barcode', 'Printed in error', 'Replaced by a reprint']

/**
 * Discard a label (damaged, misprinted) or return it unused — signed, either
 * way (z8nrz7dt48). Discarding needs a reason, and discarding a label already
 * verified on the bag clears that verification, as the legacy label screen
 * warns before it does.
 */
export function LabelUsageDialog({
  label,
  usage,
  onClose,
}: {
  label: ProductLabel | null
  usage: 'discarded' | 'unused'
  onClose: () => void
}) {
  const { user } = useAuth()
  const setUsage = useSetLabelUsage()
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const discarding = usage === 'discarded'

  useEffect(() => {
    setReason('')
    setError(null)
  }, [label?.id, usage])

  function confirm(signingToken: string) {
    if (!label) return
    setError(null)
    setUsage.mutate(
      {
        id: label.id,
        usage,
        reason: reason.trim() || undefined,
        signingToken,
        signatureMeaning: discarding ? LABEL_SIGNATURE_MEANINGS.discarded : LABEL_SIGNATURE_MEANINGS.unused,
      },
      { onSuccess: onClose, onError: e => setError(apiErrorMessage(e, 'Could not update the label.')) },
    )
  }

  return (
    <SignatureDialog
      open={!!label}
      onOpenChange={o => !o && onClose()}
      title={label ? `${discarding ? 'Discard' : 'Return'} label #${label.number}?` : 'Label'}
      description={
        label && (
          <>
            {label.din}
            {label.productCode ? ` · ${label.productCode}` : ''} —{' '}
            {discarding
              ? 'the label is destroyed and counted as discarded.'
              : 'the label was never applied and is counted as returned unused.'}
          </>
        )
      }
      confirmLabel={discarding ? 'Discard & Sign' : 'Return & Sign'}
      signerName={user?.fullName || user?.email}
      confirmDisabled={discarding && !reason.trim()}
      busy={setUsage.isPending}
      error={error}
      onConfirm={confirm}
    >
      {label && (
        <div className='space-y-3'>
          {discarding && label.usage === 'applied' && (
            <p className='rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800'>
              This label was verified after printing. Discarding it clears that post-print verification.
            </p>
          )}
          <div className='space-y-1.5'>
            <Label htmlFor='label-usage-reason'>
              Reason{discarding ? <span className='text-destructive'> *</span> : ' (optional)'}
            </Label>
            <Input
              id='label-usage-reason'
              autoFocus
              autoComplete='off'
              list={discarding ? 'label-discard-reasons' : undefined}
              value={reason}
              maxLength={500}
              onChange={e => setReason(e.target.value)}
            />
            {discarding && (
              <datalist id='label-discard-reasons'>
                {DISCARD_REASONS.map(r => (
                  <option key={r} value={r} />
                ))}
              </datalist>
            )}
          </div>
        </div>
      )}
    </SignatureDialog>
  )
}
